/* [AI] 2026-05-11: Phase B-3 Wishlist 機能
 * MVP: localStorage で商品 handle を保持
 * Phase 2: ログイン時に Customer metafield と双方向同期
 * 仕様:
 *   - data-mgh-wishlist-toggle="<product_handle>" のボタンを click でトグル
 *   - data-mgh-wishlist-count 要素は現在のお気に入り数を表示
 *   - data-mgh-wishlist-list 要素は handle 配列を JSON で出力（一覧ページ用）
 *   - mgh:wishlist:changed カスタムイベントを発火
 */

const MGH_WISHLIST_KEY = 'mgh:wishlist:v1';

class MghWishlist {
  static read() {
    try {
      const raw = localStorage.getItem(MGH_WISHLIST_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  static write(list) {
    try {
      localStorage.setItem(MGH_WISHLIST_KEY, JSON.stringify(list));
    } catch (e) {
      // ignore
    }
  }

  static has(handle) {
    return this.read().includes(handle);
  }

  static toggle(handle) {
    if (!handle) return false;
    const list = this.read();
    const idx = list.indexOf(handle);
    if (idx >= 0) {
      list.splice(idx, 1);
    } else {
      list.unshift(handle);
    }
    this.write(list);
    document.dispatchEvent(
      new CustomEvent('mgh:wishlist:changed', {
        detail: { list, handle, action: idx >= 0 ? 'removed' : 'added' },
      }),
    );
    return idx < 0;
  }

  static count() {
    return this.read().length;
  }

  /**
   * [AI] 2026-05-18: 未ログインユーザーが Wishlist を押した時のログイン訴求モーダル。
   * 文言は localStorage に翻訳済みで、layout/theme.liquid から
   * window.MGH_WISHLIST_PROMPT_STRINGS にセットされる想定（未設定時は日本語フォールバック）。
   */
  static showLoginPrompt() {
    if (document.getElementById('mgh-wishlist-login-prompt')) return;
    const strings = (window.MGH_WISHLIST_PROMPT_STRINGS) || {
      title: 'お気に入りに保存するにはログインが必要です',
      body: 'ログインすると保存した商品をデバイスをまたいで確認できます。',
      login: 'ログイン',
      register: '新規登録',
      login_or_register: 'ログイン / 新規登録',
      dismiss: '閉じる',
    };
    // [AI] 2026-05-19: URL は layout/theme.liquid 経由で routes.account_login_url /
    // routes.account_register_url を MGH_WISHLIST_PROMPT_URLS で受け取る。
    // New customer accounts では shopify.com/{shop-id}/account 形式に展開される。
    // 未設定時は legacy /account/login にフォールバック（Classic accounts 環境向け）。
    const urls = window.MGH_WISHLIST_PROMPT_URLS || {
      login: '/account/login',
      register: '/account/register',
    };
    // [AI] 2026-08-04: ボタンは1つに統一する。
    // 遷移先のアカウント画面からログインと新規登録のどちらにも進めるため、
    // 2つ並べても行き先が実質同じで選ばせる意味がない。
    // （New customer accounts では routes の解決結果自体が同一 URL になる）
    const actions = `<a class="mgh-wishlist-prompt__cta" href="${urls.login}">${strings.login_or_register || strings.login}</a>`;
    const trigger = document.activeElement;
    const wrap = document.createElement('div');
    wrap.id = 'mgh-wishlist-login-prompt';
    wrap.className = 'mgh-wishlist-prompt';
    wrap.innerHTML = `
      <div class="mgh-wishlist-prompt__backdrop" data-mgh-prompt-close></div>
      <div class="mgh-wishlist-prompt__panel" role="dialog" aria-modal="true" aria-labelledby="mgh-wishlist-prompt-title">
        <button type="button" class="mgh-wishlist-prompt__close" data-mgh-prompt-close aria-label="${strings.dismiss}">×</button>
        <h2 id="mgh-wishlist-prompt-title" class="mgh-wishlist-prompt__title">${strings.title}</h2>
        <p class="mgh-wishlist-prompt__body">${strings.body}</p>
        <div class="mgh-wishlist-prompt__actions">${actions}</div>
      </div>
    `;
    document.body.appendChild(wrap);
    const close = () => {
      wrap.remove();
      document.removeEventListener('keydown', onKey);
      // フォーカスをトリガーに戻す
      if (trigger && typeof trigger.focus === 'function') trigger.focus();
    };
    // [AI] 2026-05-19: focus trap。Tab / Shift+Tab でモーダル内を循環。Escape で閉じる。
    const onKey = (ev) => {
      if (ev.key === 'Escape') {
        close();
        return;
      }
      if (ev.key !== 'Tab') return;
      const focusables = wrap.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (ev.shiftKey && document.activeElement === first) {
        ev.preventDefault();
        last.focus();
      } else if (!ev.shiftKey && document.activeElement === last) {
        ev.preventDefault();
        first.focus();
      }
    };
    wrap.querySelectorAll('[data-mgh-prompt-close]').forEach((el) => el.addEventListener('click', close));
    document.addEventListener('keydown', onKey);
    requestAnimationFrame(() => {
      wrap.classList.add('is-open');
      // 開いた直後に「閉じる」ボタンへフォーカス
      const closeBtn = wrap.querySelector('.mgh-wishlist-prompt__close');
      if (closeBtn) closeBtn.focus();
    });
  }
}

class MghWishlistToggle extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.mghWishlistToggle;
    this.button = this.querySelector('button') || this;
    this._onClick = (e) => this.handleClick(e);
    this._onChange = () => this.syncState();
    this.button.addEventListener('click', this._onClick);
    this.syncState();
    document.addEventListener('mgh:wishlist:changed', this._onChange);
  }

  disconnectedCallback() {
    if (this.button && this._onClick) this.button.removeEventListener('click', this._onClick);
    if (this._onChange) document.removeEventListener('mgh:wishlist:changed', this._onChange);
  }

  handleClick(e) {
    if (this.tagName === 'BUTTON' || this.button === this) {
      e.preventDefault();
    }
    // [AI] 2026-05-18: 未ログインならログイン訴求モーダルを出して abort。
    // wishlist 自体は customer-level 機能（同期はフェーズ 2）のため、ログインなしの追加は許可しない。
    if (this.dataset.customerLoggedIn !== 'true') {
      MghWishlist.showLoginPrompt();
      return;
    }
    const added = MghWishlist.toggle(this.handle);
    this.button.setAttribute('aria-pressed', added ? 'true' : 'false');
  }

  syncState() {
    const has = MghWishlist.has(this.handle);
    this.button.setAttribute('aria-pressed', has ? 'true' : 'false');
    this.classList.toggle('is-active', has);
  }
}

if (!customElements.get('mgh-wishlist-toggle')) {
  customElements.define('mgh-wishlist-toggle', MghWishlistToggle);
}

class MghWishlistCount extends HTMLElement {
  connectedCallback() {
    this._onChange = () => this.update();
    this.update();
    document.addEventListener('mgh:wishlist:changed', this._onChange);
  }
  disconnectedCallback() {
    if (this._onChange) document.removeEventListener('mgh:wishlist:changed', this._onChange);
  }
  update() {
    const count = MghWishlist.count();
    this.textContent = count > 0 ? String(count) : '';
    this.hidden = count === 0;
  }
}

if (!customElements.get('mgh-wishlist-count')) {
  customElements.define('mgh-wishlist-count', MghWishlistCount);
}

// Shopify handle format: lowercase letters, digits, hyphens (Japanese allowed via punycode but Shopify normalizes to ASCII)
const MGH_WISHLIST_HANDLE_RE = /^[a-z0-9][a-z0-9-]*$/;

class MghWishlistList extends HTMLElement {
  async connectedCallback() {
    this.emptyEl = this.querySelector('[data-mgh-wishlist-empty]');
    this.gridEl = this.querySelector('[data-mgh-wishlist-grid]');
    this.countEl = this.querySelector('[data-mgh-wishlist-count-text]');
    this._onChange = () => this.render();
    await this.render();
    document.addEventListener('mgh:wishlist:changed', this._onChange);
  }
  disconnectedCallback() {
    if (this._onChange) document.removeEventListener('mgh:wishlist:changed', this._onChange);
  }
  updateCount(n) {
    if (!this.countEl) return;
    if (n === 0) {
      this.countEl.textContent = '';
      return;
    }
    // [AI] 2026-05-13: 単数/複数の文言テンプレートを Liquid 側から data 属性で受け取り
    // [AI] 2026-05-18: placeholder を {{ count }} → __COUNT__ に変更（Liquid との衝突回避）
    const tpl = n === 1 ? this.dataset.countOne : this.dataset.countOther;
    this.countEl.textContent = (tpl || '__COUNT__ items').replace('__COUNT__', String(n));
  }
  async render() {
    if (!this.gridEl) return;
    // [AI] 2026-05-11: localStorage の値を Shopify handle 形式でバリデーション
    const list = MghWishlist.read().filter(
      (h) => typeof h === 'string' && MGH_WISHLIST_HANDLE_RE.test(h),
    );
    if (list.length === 0) {
      this.gridEl.innerHTML = '';
      this.updateCount(0);
      if (this.emptyEl) this.emptyEl.hidden = false;
      return;
    }
    if (this.emptyEl) this.emptyEl.hidden = true;
    const fragments = await Promise.all(
      list.map(async (handle) => {
        try {
          const res = await fetch(`/products/${handle}?view=card`);
          if (!res.ok) return null;
          return await res.text();
        } catch (e) {
          return null;
        }
      }),
    );
    const valid = fragments.filter(Boolean).map((html) => `<li class="mgh-wishlist-page__grid-item">${html}</li>`);
    this.gridEl.innerHTML = valid.join('');
    this.updateCount(valid.length);
  }
}

if (!customElements.get('mgh-wishlist-list')) {
  customElements.define('mgh-wishlist-list', MghWishlistList);
}

window.MghWishlist = MghWishlist;
